import { useRef } from 'react'

// 구글 드라이브에서 견적서(엑셀) 파일을 골라 File 객체로 돌려주는 훅.
// 1) Google Identity Services로 drive.file 액세스 토큰을 받고
// 2) Picker(파일 선택 UI)를 그 토큰으로 열어 사용자가 파일을 고르게 한 뒤
// 3) 선택된 파일의 실제 바이트를 Drive REST API로 직접 받아 File로 감싼다.
// 업로드·AI분석은 로컬 파일 선택과 동일한 handleFilesSelected 경로를 그대로 타므로
// 서버 쪽엔 아무 변경이 없다.

const CLIENT_ID  = String(import.meta.env.VITE_GOOGLE_CLIENT_ID ?? '')
// Picker 위젯용 키와 Drive REST 호출용 키를 구글 클라우드 콘솔에서 API별로 따로 발급해
// 각각 별도 API 제한을 걸어둔 경우를 위해 두 값을 분리해서 받는다.
const PICKER_KEY = String(import.meta.env.VITE_GOOGLE_DRIVE_PICKER_API_KEY ?? '')
const DRIVE_API_KEY = String(import.meta.env.VITE_GOOGLE_DRIVE_API_KEY ?? '')
// drive.file — Picker로 사용자가 직접 고른 파일에만 접근하는 스코프.
// drive.readonly(전체 읽기)는 Google이 "제한된 스코프"로 분류해 앱 게시 검증 시 매년
// 유료 보안감사(CASA)까지 요구한다. 이 훅은 애초에 Picker로 고른 파일 하나만 받아오므로
// drive.file(민감 스코프, CASA 불필요)로도 기능이 동일해 이쪽을 쓴다.
const DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.file'
const SHEET_MIME_TYPE_LIST = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.ms-excel',                                          // .xls
  'application/vnd.google-apps.spreadsheet',                           // 네이티브 구글 시트 — export로 변환
]
const SHEET_MIME_TYPES = SHEET_MIME_TYPE_LIST.join(',')
// includeDocuments — 엑셀 외에 PDF·이미지(견적서 스캔본)도 고를 수 있게 하는 옵션(자동 생성 업로드 화면용)
const DOCUMENT_MIME_TYPES = [
  ...SHEET_MIME_TYPE_LIST,
  'application/pdf',
  'image/png',
  'image/jpeg',
].join(',')

export function isGoogleDrivePickerConfigured() {
  return !!CLIENT_ID && !!PICKER_KEY
}

// 스크립트 로딩은 앱 전체에서 한 번만 — 여러 번 호출돼도 같은 Promise를 공유한다.
let gsiScriptPromise: Promise<void> | null = null
let gapiScriptPromise: Promise<void> | null = null

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`)
    if (existing) { resolve(); return }
    const el = document.createElement('script')
    el.src = src
    el.async = true
    el.onload = () => resolve()
    el.onerror = () => reject(new Error(`스크립트를 불러오지 못했습니다: ${src}`))
    document.head.appendChild(el)
  })
}

function ensureGsiLoaded(): Promise<void> {
  gsiScriptPromise ??= loadScript('https://accounts.google.com/gsi/client')
  return gsiScriptPromise
}

function ensurePickerLoaded(): Promise<void> {
  gapiScriptPromise ??= loadScript('https://apis.google.com/js/api.js').then(
    () => new Promise<void>(resolve => window.gapi.load('picker', () => resolve())),
  )
  return gapiScriptPromise
}

// 사용자가 구글 인증 창을 스스로 닫은 경우 — Picker를 취소한 것과 같은 정상적인 취소라
// 에러 배너를 띄우지 않고 조용히 null로 취급한다(open()에서 구분해서 처리).
class DriveAuthCancelledError extends Error {}

function requestAccessToken(): Promise<string> {
  return new Promise((resolve, reject) => {
    try {
      const client = window.google.accounts.oauth2.initTokenClient({
        client_id: CLIENT_ID,
        scope: DRIVE_SCOPE,
        callback: (res: { access_token?: string; error?: string }) => {
          if (res.error || !res.access_token) reject(new Error('구글 드라이브 접근 권한을 받지 못했습니다.'))
          else resolve(res.access_token)
        },
        error_callback: () => reject(new DriveAuthCancelledError()),
      })
      client.requestAccessToken({ prompt: '' })
    } catch {
      reject(new Error('구글 인증을 시작하지 못했습니다.'))
    }
  })
}

// 선택된 문서 하나를 열어 실제 파일(File)로 받는다.
// 네이티브 구글 시트는 alt=media로 못 받아 export(xlsx 변환) 엔드포인트를 대신 쓴다.
async function fetchPickedFile(doc: { id: string; name: string; mimeType: string }, accessToken: string): Promise<File> {
  const isNativeSheet = doc.mimeType === 'application/vnd.google-apps.spreadsheet'
  const params = new URLSearchParams(
    isNativeSheet
      ? { mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }
      : { alt: 'media' },
  )
  // Bearer 토큰만으로도 인증되지만, Drive API 전용으로 발급·제한된 키가 있으면 함께 실어
  // 그 키의 API 제한(Drive API만 허용) 조건과 어긋나지 않게 한다.
  if (DRIVE_API_KEY) params.set('key', DRIVE_API_KEY)
  const url = `https://www.googleapis.com/drive/v3/files/${doc.id}${isNativeSheet ? '/export' : ''}?${params}`

  const res = await fetch(url, { headers: { Authorization: `Bearer ${accessToken}` } })
  if (!res.ok) throw new Error('구글 드라이브에서 파일을 받아오지 못했습니다.')
  const blob = await res.blob()
  const fileName = isNativeSheet && !/\.xlsx$/i.test(doc.name) ? `${doc.name}.xlsx` : doc.name
  return new File([blob], fileName, { type: blob.type })
}

function openPickerDialog(accessToken: string, includeDocuments: boolean): Promise<{ id: string; name: string; mimeType: string } | null> {
  return new Promise((resolve, reject) => {
    try {
      const view = new window.google.picker.DocsView(
        includeDocuments ? window.google.picker.ViewId.DOCS : window.google.picker.ViewId.SPREADSHEETS,
      )
        .setMimeTypes(includeDocuments ? DOCUMENT_MIME_TYPES : SHEET_MIME_TYPES)
        .setIncludeFolders(true)

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(accessToken)
        .setDeveloperKey(PICKER_KEY)
        .setLocale('ko')
        .setCallback((data: { action: string; docs?: { id: string; name: string; mimeType: string }[] }) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const doc = data.docs?.[0]
            resolve(doc ? { id: doc.id, name: doc.name, mimeType: doc.mimeType } : null)
          } else if (data.action === window.google.picker.Action.CANCEL) {
            resolve(null)
          }
        })
        .build()
      picker.setVisible(true)
    } catch {
      reject(new Error('파일 선택 창을 열지 못했습니다.'))
    }
  })
}

/** 반환값: 사용자가 고른 엑셀 File, 또는 취소했으면 null. 설정 미완료/오류는 throw. */
export function useGoogleDrivePicker() {
  const openingRef = useRef(false)

  const open = async (options?: { includeDocuments?: boolean }): Promise<File | null> => {
    if (!isGoogleDrivePickerConfigured()) {
      throw new Error('구글 드라이브 연동 설정이 필요합니다.')
    }
    if (openingRef.current) return null
    openingRef.current = true
    try {
      await Promise.all([ensureGsiLoaded(), ensurePickerLoaded()])
      const accessToken = await requestAccessToken().catch(e => {
        if (e instanceof DriveAuthCancelledError) return null
        throw e
      })
      if (!accessToken) return null
      const doc = await openPickerDialog(accessToken, !!options?.includeDocuments)
      if (!doc) return null
      return await fetchPickedFile(doc, accessToken)
    } finally {
      openingRef.current = false
    }
  }

  return { open, isConfigured: isGoogleDrivePickerConfigured() }
}

// 최소 타입 선언 — 공식 @types 패키지를 새로 추가하지 않고 이 훅 안에서만 쓰는 형태로 한정한다.
declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string
            scope: string
            callback: (res: { access_token?: string; error?: string }) => void
            error_callback?: () => void
          }) => { requestAccessToken: (opts?: { prompt?: string }) => void }
        }
      }
      picker: {
        DocsView: new (viewId: unknown) => {
          setMimeTypes: (m: string) => any
          setIncludeFolders: (b: boolean) => any
        }
        ViewId: { SPREADSHEETS: unknown; DOCS: unknown }
        Action: { PICKED: string; CANCEL: string }
        PickerBuilder: new () => {
          addView: (v: unknown) => any
          setOAuthToken: (t: string) => any
          setLocale: (l: string) => any
          setDeveloperKey: (k: string) => any
          setCallback: (cb: (data: any) => void) => any
          build: () => { setVisible: (v: boolean) => void }
        }
      }
    }
    gapi: { load: (mod: string, cb: () => void) => void }
  }
}
